import React, { useEffect, useState } from 'react';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { BottomNavigation, BottomNavigationAction, Box, Button, Collapse, Paper, Table, TableBody, TableHead, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { calculateOverallAttendancePercentage, calculateSubjectAttendancePercentage, groupAttendanceBySubject } from '../../components/attendanceCalculator';
import CustomBarChart from '../../components/CustomBarChart';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { StyledTableCell, StyledTableRow } from '../../components/styles';
import LoadingSpinner from '../../components/LoadingSpinner';

const ViewStdAttendance = () => {
    const dispatch = useDispatch();
    const { userDetails, currentUser, loading, error } = useSelector((state) => state.user);
    const [openStates, setOpenStates] = useState({});
    const [selectedSection, setSelectedSection] = useState('table');

    useEffect(() => {
        dispatch(getUserDetails(currentUser._id, "Student"));
    }, [dispatch, currentUser._id]);

    const handleOpen = (subId) => {
        setOpenStates(prev => ({ ...prev, [subId]: !prev[subId] }));
    };

    const attendanceBySubject = groupAttendanceBySubject(userDetails?.attendance || []);
    const overallAttendancePercentage = calculateOverallAttendancePercentage(userDetails?.attendance || []);

    const subjectData = Object.entries(attendanceBySubject).map(([subName, { present, sessions }]) => ({
        subject: subName,
        attendancePercentage: calculateSubjectAttendancePercentage(present, sessions),
        totalClasses: sessions,
        attendedClasses: present
    }));

    const handleSectionChange = (event, newSection) => {
        setSelectedSection(newSection);
    };

    const renderTableSection = () => (
        <>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: 'teal', fontWeight: 'bold' }}>
                Attendance Details
            </Typography>
            <Table>
                <TableHead>
                    <StyledTableRow>
                        <StyledTableCell sx={{ color: 'gold', fontWeight: 'bold' }}>Subject</StyledTableCell>
                        <StyledTableCell sx={{ color: 'gold', fontWeight: 'bold' }}>Present</StyledTableCell>
                        <StyledTableCell sx={{ color: 'gold', fontWeight: 'bold' }}>Total Sessions</StyledTableCell>
                        <StyledTableCell sx={{ color: 'gold', fontWeight: 'bold' }}>Attendance Percentage</StyledTableCell>
                        <StyledTableCell align="center" sx={{ color: 'gold', fontWeight: 'bold' }}>Details</StyledTableCell>
                    </StyledTableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(attendanceBySubject).map(([subName, { present, allData, subId, sessions }], index) => {
                        const percentage = calculateSubjectAttendancePercentage(present, sessions);
                        
                        return (
                            <React.Fragment key={subId}>
                                <StyledTableRow>
                                    <StyledTableCell sx={{ color: 'teal' }}>{subName}</StyledTableCell>
                                    <StyledTableCell sx={{ color: 'teal' }}>{present}</StyledTableCell>
                                    <StyledTableCell sx={{ color: 'teal' }}>{sessions}</StyledTableCell>
                                    <StyledTableCell sx={{ color: 'teal' }}>{sessions > 0 ? `${percentage.toFixed(2)}%` : 'N/A'}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Button 
                                            variant="contained"
                                            onClick={() => handleOpen(subId)}
                                            endIcon={openStates[subId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            sx={{
                                                backgroundColor: 'gold',
                                                color: 'teal',
                                                '&:hover': {
                                                    backgroundColor: 'darkgoldenrod',
                                                    color: 'white'
                                                },
                                                transition: 'all 0.3s ease',
                                                boxShadow: 3
                                            }}
                                        >
                                            {openStates[subId] ? 'Hide' : 'Show'} Details
                                        </Button>
                                    </StyledTableCell>
                                </StyledTableRow>
                                <StyledTableRow>
                                    <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                                        <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 1 }}>
                                                <Typography variant="h6" gutterBottom sx={{ color: 'teal', fontWeight: 'bold' }}>
                                                    Daily Attendance Records
                                                </Typography>
                                                <Table size="small">
                                                    <TableHead>
                                                        <StyledTableRow>
                                                            <StyledTableCell sx={{ color: 'gold', fontWeight: 'bold' }}>Date</StyledTableCell>
                                                            <StyledTableCell align="right" sx={{ color: 'gold', fontWeight: 'bold' }}>Status</StyledTableCell>
                                                        </StyledTableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {allData.map((data, index) => {
                                                            const date = new Date(data.date);
                                                            const isValidDate = !isNaN(date.getTime());
                                                            return (
                                                                <StyledTableRow key={index}>
                                                                    <StyledTableCell sx={{ color: 'teal' }}>
                                                                        {isValidDate ? date.toLocaleDateString() : 'Invalid Date'}
                                                                    </StyledTableCell>
                                                                    <StyledTableCell 
                                                                        align="right"
                                                                        sx={{ 
                                                                            color: data.status.toLowerCase() === 'present' 
                                                                                ? 'success.main' 
                                                                                : 'error.main',
                                                                            fontWeight: 'bold'
                                                                        }}
                                                                    >
                                                                        {data.status}
                                                                    </StyledTableCell>
                                                                </StyledTableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        </Collapse>
                                    </StyledTableCell>
                                </StyledTableRow>
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'teal', fontWeight: 'bold' }}>
                    Overall Attendance: {overallAttendancePercentage.toFixed(2)}%
                </Typography>
            </Box>
        </>
    );

    const renderChartSection = () => (
        <Box sx={{ height: '500px', mt: 4 }}>
            <CustomBarChart chartData={subjectData} dataKey="attendancePercentage" />
        </Box>
    );

    if (loading) return <LoadingSpinner />;
    if (error) return <Typography color="error">Error: {error}</Typography>;

    return (
        <Box sx={{ p: 3 }}>
            {userDetails?.attendance?.length > 0 ? (
                <>
                    {selectedSection === 'table' && renderTableSection()}
                    {selectedSection === 'chart' && renderChartSection()}

                    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
                        <BottomNavigation 
                            value={selectedSection} 
                            onChange={handleSectionChange} 
                            showLabels
                            sx={{ backgroundColor: 'teal' }}
                        >
                            <BottomNavigationAction
                                label="Table"
                                value="table"
                                icon={selectedSection === 'table' ? <TableChartIcon sx={{ color: 'gold' }} /> : <TableChartOutlinedIcon sx={{ color: 'gold' }} />}
                                sx={{ color: 'gold' }}
                            />
                            <BottomNavigationAction
                                label="Chart"
                                value="chart"
                                icon={selectedSection === 'chart' ? <InsertChartIcon sx={{ color: 'gold' }} /> : <InsertChartOutlinedIcon sx={{ color: 'gold' }} />}
                                sx={{ color: 'gold' }}
                            />
                        </BottomNavigation>
                    </Paper>
                </>
            ) : (
                <Typography variant="h6" textAlign="center" sx={{ mt: 4, color: 'teal', fontWeight: 'bold' }}>
                    No attendance records found
                </Typography>
            )}
        </Box>
    );
};

export default ViewStdAttendance;